import React, { useState } from "react";
import { decimalToPercentage } from "../../../lib/tierHelper";
import { ReactComponent as TooltipIcon } from "svg/question-line.svg";
import CopyTalentsButton from "../../Common/CopyTalentsButton";
import {
  getFormattedNames,
  getHeroNameFromSelectedTree,
  getHeroTalentIconURL,
  getMainStatData,
  getParsedSelectedTalents,
  parseBuildRows,
} from "../../../lib/wowHelpers";
import { ReactComponent as ViewTreeIcon } from "svg/node-tree.svg";
import { ReactComponent as ViewListIcon } from "svg/list-check-2.svg";
import { TooltipContainer } from "@outplayed/tooltips";
import TalentsList from "./TalentsList";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import WOWTalentTreeSection from "../Builds/TalentTreeSection";
import { validParams } from "../../../query_parameters/valid_parameters";
import SubtitleAndDescription from "../../Common/SubtitleAndDescription";
import { WoWContext } from "../../App";

const TalentsContainer = (props) => {
  const { classType, encounterType, specType, index, talentData, totalCount, selectedHero } = props;
  const [viewTree, setViewTree] = useState(index === 0);

  const toggleViewTree = () => {
    setViewTree(!viewTree);
  };

  const specs = validParams.builds.specs[classType];
  const allTrees = {};
  for (let i = 0; i < 4; i++) {
    const { data: specData } = useJSONFetcher(`https://static.bigbrain.gg/wow/tree/v14/${classType}_${specs[i]?.value}.json`, {
      ssr: true,
    });

    allTrees[specs[i]?.value] = specData;
  }

  const classAndSpecTree = allTrees[specType];

  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);
  const heroName = getHeroNameFromSelectedTree(selectedHero, displayClassName, displaySpecName);
  const talents = talentData ? talentData.talent.split("-") : [];
  const { selectedTalents, selectedTalentNodeIds } = getParsedSelectedTalents(talents);
  const specRows = parseBuildRows(classAndSpecTree?.specNodes, selectedTalents, "all", true);
  const classRows = parseBuildRows(classAndSpecTree?.classNodes, selectedTalents, "all", true);
  const heroRows = parseBuildRows(classAndSpecTree?.hero[selectedHero].nodes, selectedTalents, "all", true);
  const popularity = decimalToPercentage(talentData.count / totalCount);
  const { mainStat, statLabel, statTooltip } = getMainStatData(
    encounterType,
    specType,
    talentData.max_key,
    talentData.hps,
    talentData.dps,
  );
  const popLabel = encounterType === "mythic" ? "Dungeon Runs" : "Parses";
  const popTooltip =
    encounterType === "mythic"
      ? "Dungeon Runs is directly tied to Popularity, displayed as the total number of completed dungeons by each spec using this talent build in the top rankings of Mythic+ Dungeons."
      : "Parses is directly tied to Popularity, displayed as the total number of completed encounters by a spec using this talent build in the top rankings of the Raid.";

  const talentsContextValue = {
    allTrees,
    selectedTalents: talents,
    specType,
    selectedHero,
  };

  if (!classAndSpecTree) {
    return null;
  }

  return (
    <WoWContext.Provider value={talentsContextValue}>
      <div className={`talents-container ${index === 0 ? "recommended" : ""}`}>
        <div className="header-section">
          <SubtitleAndDescription
            subtitle={`#${index + 1} ${displaySpecName} ${displayClassName} Talent Tree`}
            description={index === 0 ? "Recommended" : ""}
            className="optional-talents-subtitle"
          />
          <div className="stats-and-buttons-section">
            <div className="stats-section">
              <div className="stat-box">
                <div className="stat-value">{mainStat}</div>
                <div className="stat-label">
                  {statLabel}
                  <TooltipContainer tooltipInfo={<div>{statTooltip}</div>}>
                    <div className="tooltip-container">
                      <TooltipIcon className="tooltip-icon" />
                    </div>
                  </TooltipContainer>
                </div>
              </div>
              <div className="stat-box popularity">
                <div className="stat-value">
                  {popularity} <span className="stat-label">Talent Popularity</span>
                </div>
                <div className="stat-label">
                  {talentData.count} {popLabel}
                  <TooltipContainer tooltipInfo={<div>{popTooltip}</div>}>
                    <div className="tooltip-container">
                      <TooltipIcon className="tooltip-icon" />
                    </div>
                  </TooltipContainer>
                </div>
              </div>
            </div>
            <div className="buttons-section">
              <div className="view-tree-button" onClick={() => toggleViewTree()}>
                {viewTree ? (
                  <div>
                    <ViewListIcon />
                    View Talents
                  </div>
                ) : (
                  <div>
                    <ViewTreeIcon />
                    View Tree
                  </div>
                )}
              </div>
              <CopyTalentsButton label="Copy Talents" />
            </div>
          </div>
        </div>
        <div className="horizontal-divider" />
        {viewTree ? (
          <WOWTalentTreeSection
            classType={classType}
            closeModal={() => setIsModalOpen(false)}
            encounterType={encounterType}
            isModal={true}
            selectedTalents={talents}
            classAndSpecTree={classAndSpecTree}
            specType={specType}
            popularity={popularity}
            mainStat={mainStat}
            statLabel={statLabel}
            statTooltip={statTooltip}
            allSpecsTrees={allTrees}
            className="talent-tree-accordion"
            hideHeader
            selectedHero={selectedHero}
          />
        ) : (
          <div className="talents-lists-container">
            <TalentsList
              iconImg={`https://static.bigbrain.gg/wow/class-icons/ClassIcon_${dataClassName}.jpg`}
              label={displayClassName}
              rows={classRows}
              specType={specType}
              classType={classType}
              selectedNodes={selectedTalentNodeIds}
            />
            <div className="talents-list-divider" />
            <TalentsList
              iconImg={`https://static.bigbrain.gg/wow/spec-icons/${dataClassName}-${dataSpecName}.jpg`}
              label={displaySpecName}
              rows={specRows}
              specType={specType}
              classType={classType}
              selectedNodes={selectedTalentNodeIds}
            />
            <TalentsList
              iconImg={getHeroTalentIconURL(heroName)}
              label={heroName}
              rows={heroRows}
              specType={specType}
              classType={classType}
              selectedNodes={selectedTalentNodeIds}
              heroList
            />
          </div>
        )}
      </div>
    </WoWContext.Provider>
  );
};

export default TalentsContainer;
